import moment from "moment";
import { minValue, maxValue } from "@/util/dateUtil";

const DEBUG = "debug";
const INFO = "info";
const WARNING = "warning";
const ERROR = "error";

const IDX_TIMESTAMP = "idx_timestamp";
const IDX_LEVEL = "idx_level";
const IDX_ROUTE = "idx_route";
const IDX_COMPONENT = "idx_component"

const DATABASE = "sbDiagnostics";
const LOG = "log";
const VERSION = 1;

class QueryBuilder {
    constructor() {
        this.__index = IDX_TIMESTAMP;
        this.__startDate = minValue;
        this.__endDate = maxValue;
    }

    forLevel(level) {
        if (level) {
            this.__index = IDX_LEVEL;
            this.__level = level;
        } else {
            this.__index = IDX_TIMESTAMP;
        }

        return this;
    }

    after(after) {
        if (after) {
            this.__startDate = moment(after).toDate();
        } else {
            this.__startDate = minValue
        }
        return this;
    }

    before(before) {
        if (before) {
            this.__endDate = moment(before).toDate();
        } else {
            this.__endDate = maxValue;
        }
        return this;
    }

    __build() {
        switch (this.__index) {
            case IDX_LEVEL:
                return IDBKeyRange.bound([this.__level, this.__startDate], [this.__level, this.__endDate]);
            case IDX_TIMESTAMP:
                return IDBKeyRange.bound(this.__startDate, this.__endDate);
            default:
                return undefined;
        }
    }
}

const getDb = (create = false) => new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DATABASE, VERSION);

    if (create) {
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(LOG)) {
                const log = db.createObjectStore(LOG, {
                    keyPath: "id",
                    autoIncrement: true
                });

                log.createIndex(IDX_TIMESTAMP, "timestamp");
                log.createIndex(IDX_LEVEL, ["level", "timestamp"]);
                log.createIndex(IDX_ROUTE, ["route", "timestamp"]);
                log.createIndex(IDX_COMPONENT, ["component", "timestamp"]);
            }
        }
    }

    request.onsuccess = () => {
        resolve(request.result);
    };

    request.onerror = () => {
        reject(request.error);
    };
})

const clear = async () => {
    try {
        const db = await getDb();
        const transaction = db.transaction(LOG, "readwrite");
        const log = transaction.objectStore(LOG);

        await new Promise(resolve => {
            const request = log.clear();

            request.onsuccess = () => {
                console.log("Diagnostics database cleared.");
                resolve();
            };

            request.onerror = () => {
                console.error(`Unable to clear diagnostics database: ${error}`)
                resolve();
            };
        });
    } catch (error) {
        console.error(`Unable to clear diagnostics database: ${error}`)
    }
}

const enable = async () => {
    try {
        await getDb(true);
        console.log("Diagnostics enabled.");
    } catch (error) {
        console.error(`Unable to enable diagnostics: ${error}`);
    }
}

const log = async (level, message, route = null, component = null, data = null) => {
    try {
        const db = await getDb();
        const transaction = db.transaction(LOG, "readwrite");
        const log = transaction.objectStore(LOG);

        const logEntry = {
            timestamp: moment.utc().toDate(),
            level,
            message
        };

        if (route) {
            logEntry.route = route;
        }

        if (component) {
            logEntry.component = component;
        }

        if (data) {
            logEntry.data = data;
        }

        await new Promise(resolve => {
            const request = log.add(logEntry);

            request.onsuccess = resolve;

            request.onerror = () => {
                console.log(`Unable to log diagnostic event.  Original log entry was: '${message}'.`);
                resolve();
            };
        });
    } catch {
        console.log(`Unable to log diagnostic event.  Original log entry was: '${message}'.`);
    }
}

const get = async (query = new QueryBuilder()) => {
    try {
        const db = await getDb();
        const transaction = db.transaction(LOG);
        const log = transaction.objectStore(LOG);
        const index = log.index(query.__index);

        const result = await new Promise(resolve => {
            const request = index.getAll(query.__build());

            request.onsuccess = () => {
                if (request.result !== undefined) {
                    resolve(request.result);
                } else {
                    resolve([]);
                }
            };

            request.onerror = (error) => {
                console.log(`Unable to query diagnostics database: ${error}`);
                resolve([]);
            };
        });

        return result
    } catch (error) {
        console.log(`Unable to query diagnostics database: ${error}`);
        return [];
    }
}

const isSupported = () => !!window.indexedDB;

export {
    DEBUG,
    INFO,
    WARNING,
    ERROR,
    QueryBuilder
}

export default {
    isSupported,
    enable,
    clear,
    log,
    get
}